<script lang="ts">
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    getSettings,
    patchSettings,
    sendTestEmail,
    ApiError,
  } from "../lib/api";
  import type {
    AdminSettings,
    PatchSettingsRequest,
  } from "@diy-avatar/shared/admin";

  let loaded = false;
  let error: string | null = null;
  let info: string | null = null;
  let saving = false;
  let testing = false;
  let testResult: string | null = null;

  // SMTP form state.
  let smtpHost = "";
  let smtpPort = "";
  let smtpSecure = false;
  let smtpUser = "";
  let smtpFrom = "";
  let smtpPass = ""; // blank keeps existing
  let passConfigured = false;

  // Recipients.
  let notifyContact = "";
  let notifyShop = "";
  let notifyPayment = "";

  // Altcha.
  let hmacConfigured = false;
  let altchaHmac = ""; // blank keeps existing

  // Demo.
  let demoEnabled = false;

  // Beta phase.
  let betaEnabled = false;

  // Email footer (appended to every outgoing email; localized).
  let mailFooterDe = "";
  let mailFooterEn = "";

  // Test email recipient override.
  let testTo = "";

  function apply(s: AdminSettings) {
    smtpHost = s.smtp.host;
    smtpPort = s.smtp.port;
    smtpSecure = s.smtp.secure;
    smtpUser = s.smtp.user;
    smtpFrom = s.smtp.from;
    passConfigured = s.smtp.passSet;
    notifyContact = s.notify.contact;
    notifyShop = s.notify.shop;
    notifyPayment = s.notify.payment;
    hmacConfigured = s.altcha.hmacSet;
    demoEnabled = s.demo.enabled;
    betaEnabled = s.beta?.enabled ?? false;
    mailFooterDe = s.mail.footerDe;
    mailFooterEn = s.mail.footerEn;
    smtpPass = "";
    altchaHmac = "";
  }

  async function load() {
    try {
      const res = await getSettings();
      apply(res.settings);
      loaded = true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "load failed";
    }
  }

  async function save() {
    saving = true;
    info = null;
    error = null;
    const patch: PatchSettingsRequest = {
      smtp: {
        host: smtpHost,
        port: smtpPort,
        secure: smtpSecure,
        user: smtpUser,
        from: smtpFrom,
      },
      notify: {
        contact: notifyContact,
        shop: notifyShop,
        payment: notifyPayment,
      },
      demo: {
        enabled: demoEnabled,
      },
      beta: {
        enabled: betaEnabled,
      },
      mail: {
        footerDe: mailFooterDe,
        footerEn: mailFooterEn,
      },
    };
    // Only send secrets when the operator typed a new value.
    if (smtpPass.length > 0) patch.smtp!.pass = smtpPass;
    if (altchaHmac.length > 0) patch.altcha = { hmacKey: altchaHmac };
    try {
      const res = await patchSettings(patch);
      apply(res.settings);
      info = "Gespeichert.";
    } catch (err) {
      error = err instanceof Error ? err.message : "save failed";
    } finally {
      saving = false;
    }
  }

  async function test() {
    testing = true;
    testResult = null;
    try {
      const res = await sendTestEmail(testTo.length > 0 ? { to: testTo } : {});
      testResult = res.ok
        ? "Test-E-Mail gesendet."
        : `Fehler: ${res.error ?? "unbekannt"}`;
    } catch (err) {
      testResult =
        err instanceof ApiError
          ? `Fehler: ${err.message}`
          : err instanceof Error
            ? err.message
            : "Test fehlgeschlagen";
    } finally {
      testing = false;
    }
  }

  onMount(load);
</script>

<section>
  <h1>Einstellungen</h1>
  {#if error}
    <p class="error" data-testid="error">{error}</p>
  {/if}

  {#if loaded}
    <form on:submit|preventDefault={save}>
      <fieldset>
        <legend>SMTP-Versand</legend>
        <label>
          Host
          <input type="text" bind:value={smtpHost} placeholder="smtp.example.com" data-testid="smtp-host" />
        </label>
        <label>
          Port
          <input type="text" inputmode="numeric" bind:value={smtpPort} placeholder="587" data-testid="smtp-port" />
        </label>
        <label class="check">
          <input type="checkbox" bind:checked={smtpSecure} data-testid="smtp-secure" />
          Implizites TLS (aus = STARTTLS/Klartext)
        </label>
        <label>
          Benutzer
          <input type="text" bind:value={smtpUser} autocomplete="off" data-testid="smtp-user" />
        </label>
        <label>
          Passwort
          <input
            type="password"
            bind:value={smtpPass}
            autocomplete="new-password"
            placeholder={passConfigured ? "•••••• (gesetzt — leer lassen, um zu behalten)" : "nicht gesetzt"}
            data-testid="smtp-pass"
          />
          <span class="hint" class:on={passConfigured}>
            {passConfigured ? "konfiguriert" : "nicht konfiguriert"}
          </span>
        </label>
        <label>
          Absender (From)
          <input type="text" bind:value={smtpFrom} placeholder="noreply@example.com" data-testid="smtp-from" />
        </label>
      </fieldset>

      <fieldset>
        <legend>Empfänger nach Zweck</legend>
        <label>
          Kontaktformular (notify.contact)
          <input type="email" bind:value={notifyContact} data-testid="notify-contact" />
        </label>
        <label>
          Kaufinteresse / Shop (notify.shop)
          <input type="email" bind:value={notifyShop} data-testid="notify-shop" />
        </label>
        <label>
          Zahlungs-Benachrichtigungen (notify.payment)
          <input type="email" bind:value={notifyPayment} data-testid="notify-payment" />
        </label>
      </fieldset>

      <fieldset>
        <legend>Altcha</legend>
        <label>
          HMAC-Schlüssel
          <input
            type="password"
            bind:value={altchaHmac}
            autocomplete="new-password"
            placeholder={hmacConfigured ? "•••••• (gesetzt — leer lassen, um zu behalten)" : "wird beim ersten Gebrauch automatisch erzeugt"}
            data-testid="altcha-hmac"
          />
          <span class="hint" class:on={hmacConfigured}>
            {hmacConfigured ? "konfiguriert" : "nicht konfiguriert"}
          </span>
        </label>
      </fieldset>

      <fieldset>
        <legend>Demo</legend>
        <label class="check">
          <input type="checkbox" bind:checked={demoEnabled} data-testid="demo-enabled" />
          Öffentliche Demo aktivieren (3-Tage-Demo-Token + Demo-Login)
        </label>
        <p class="hint">
          Aus = produktionssicher: kein Demo-Token, kein Demo-Login. Beim
          Einschalten werden ein Demo-Schulträger, eine Demo-Schule, ein
          Demo-Raum und ein Demo-Schüler-Konto angelegt.
        </p>
      </fieldset>

      <fieldset>
        <legend>Beta-Phase</legend>
        <label class="check">
          <input type="checkbox" bind:checked={betaEnabled} data-testid="beta-enabled" />
          Beta-Phase aktivieren
        </label>
        <p class="hint">
          Blendet die Beta-Werbung und das Teilnahme-Formular auf der
          öffentlichen Website ein/aus.
        </p>
      </fieldset>

      <fieldset>
        <legend>E-Mail-Fußzeile</legend>
        <p class="hint">
          Wird an jede ausgehende E-Mail angehängt. Die Sprache der Empfängerin
          bzw. des Empfängers bestimmt, welche Fußzeile verwendet wird. Leer
          lassen = keine Fußzeile.
        </p>
        <label>
          Fußzeile (Deutsch)
          <textarea
            rows="4"
            bind:value={mailFooterDe}
            data-testid="mail-footer-de"
          ></textarea>
        </label>
        <label>
          Footer (English)
          <textarea
            rows="4"
            bind:value={mailFooterEn}
            data-testid="mail-footer-en"
          ></textarea>
        </label>
      </fieldset>

      <button type="submit" disabled={saving} data-testid="save">
        {saving ? "Speichern…" : "Speichern"}
      </button>
      {#if info}<p class="info" data-testid="info">{info}</p>{/if}
    </form>

    <div class="test-box">
      <h2>Test-E-Mail</h2>
      <label>
        Empfänger (optional, sonst notify.contact)
        <input type="email" bind:value={testTo} placeholder="test@example.com" data-testid="test-to" />
      </label>
      <button type="button" on:click={test} disabled={testing} data-testid="send-test">
        {testing ? "Sende…" : "Test-E-Mail senden"}
      </button>
      {#if testResult}<p class="info" data-testid="test-result">{testResult}</p>{/if}
    </div>
  {:else if !error}
    <p>Laden…</p>
  {/if}
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  h2 {
    margin: 0 0 0.75rem;
    font-size: 1.05rem;
  }
  form,
  .test-box {
    display: grid;
    gap: 0.75rem;
    max-width: 520px;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.25rem;
  }
  fieldset {
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.75rem;
    display: grid;
    gap: 0.6rem;
  }
  legend {
    color: #10b981;
    padding: 0 0.4rem;
    font-weight: 600;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #cbd5e1;
  }
  label.check {
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
  }
  input[type="text"],
  input[type="email"],
  input[type="password"],
  textarea {
    padding: 0.45rem 0.55rem;
    background: #0b1220;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
  }
  textarea {
    font: inherit;
    resize: vertical;
  }
  .hint {
    font-size: 0.75rem;
    color: #9ca3af;
  }
  .hint.on {
    color: #6ee7b7;
  }
  button {
    padding: 0.5rem 0.8rem;
    background: #10b981;
    color: #04221a;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    justify-self: start;
  }
  .info {
    color: #6ee7b7;
  }
  .error {
    color: #fca5a5;
  }
</style>
